/**
 * Master Data File - Single Source of Truth
 * 所有部門、階段、工序的統一定義
 * All other files should import from here
 */

import { Department, Stage, Order, Material } from './types';

// Import additional types for adapters
interface UnifiedOrder {
  id: string;
  customer: string;
  customerContact: string;
  product: string;
  quantity: number;
  completedQuantity: number;
  dimensions: string;
  specifications: string;
  priority: "low" | "medium" | "high" | "urgent";
  orderDate: string;
  dueDate: string;
  plannedStart: string;
  plannedEnd: string;
  currentStageId: string;
  overallProgress: number;
  stages: any[];
  deliveryAddress: string;
  estimatedCost?: number; // Make optional since new Order interface doesn't have this
  notes: string;
}

interface OrderData {
  orderId: string;
  customer: string;
  product: string;
  quantity: number;
  priority: "high" | "medium" | "low" | "urgent";
  progress: number;
  start: string;
  end: string;
  dueDate: string;
  stages: any[];
}

// ===========================
// 1️⃣ DEPARTMENTS - 部門定義
// ===========================
export const departments: Department[] = [
  // 準備材料階段
  { 
    id: 'purchasing', 
    name: '採購部', 
    color: 'bg-amber-500', 
    description: '軸心與原料採購、供應商協調' 
  },
  { 
    id: 'warehouse', 
    name: '倉庫部', 
    color: 'bg-teal-500', 
    description: '進出貨、庫存與過濾作業' 
  },
  
  // 生產階段
  { 
    id: 'rubberizing', 
    name: '包膠部', 
    color: 'bg-pink-500', 
    description: '輪芯前處理、包膠與硫化' 
  },
  { 
    id: 'lathe', 
    name: '車床部', 
    color: 'bg-indigo-500', 
    description: '粗車、研磨與特殊加工' 
  },
  
  // 品檢階段
  { 
    id: 'quality', 
    name: '品管部', 
    color: 'bg-purple-600', 
    description: '尺寸/外觀檢驗與出貨標籤' 
  },
  { 
    id: 'packaging', 
    name: '包裝部', 
    color: 'bg-gray-600', 
    description: '清潔與最終包裝' 
  }
];

// ===========================
// 2️⃣ STAGES - 階段定義
// ===========================
export const stages: Stage[] = [
  { 
    id: 'material-preparation', 
    name: '準備材料', 
    order: 1, 
    responsibleDepartments: ['purchasing', 'warehouse'], 
    color: 'bg-blue-500', 
    description: '軸心採購、原料準備與倉庫管理' 
  },
  { 
    id: 'production', 
    name: '生產', 
    order: 2, 
    responsibleDepartments: ['rubberizing', 'lathe'], 
    color: 'bg-green-500', 
    description: '包膠處理與車床加工作業' 
  },
  { 
    id: 'quality-inspection', 
    name: '品檢', 
    order: 3, 
    responsibleDepartments: ['quality', 'packaging'], 
    color: 'bg-purple-500', 
    description: '品質檢驗與產品包裝' 
  }
];

// ===========================
// 3️⃣ WORK TEMPLATES - 工序範本
// ===========================
export const workItemTemplates = {
  /** 採購部 */
  purchasing: [
    { id: 'pur-001', name: '採購軸心', description: '依規格採購鐵輪軸心', order: 1 },
    { id: 'pur-002', name: '採購原料', description: '採購橡膠與化學藥劑原料', order: 2 }
  ],

  /** 包膠部 */
  rubberizing: [
    { id: 'rub-001', name: '舊輪除膠', description: '去除舊輪殘膠', order: 1 },
    { id: 'rub-002', name: '鐵輪除鏽', description: '清除鐵輪鏽蝕', order: 2 },
    { id: 'rub-003', name: '鐵輪上藥劑', description: '塗佈黏著劑／底漆', order: 3 },
    { id: 'rub-004', name: '出片', description: '橡膠片裁切／預成型', order: 4 },
    { id: 'rub-005', name: '包膠', description: '將橡膠片纏繞包覆輪芯', order: 5 },
    { id: 'rub-006', name: '束布', description: '纏束強化布層', order: 6 },
    { id: 'rub-007', name: '加硫', description: '熱壓硫化固化橡膠', order: 7 }
  ],

  /** 倉庫部 */
  warehouse: [
    { id: 'whs-001', name: '進貨', description: '接收原料／零件進庫', order: 1 },
    { id: 'whs-002', name: '出貨', description: '成品出庫／物流安排', order: 2 },
    { id: 'whs-003', name: '過濾', description: '原料雜質過濾／分級', order: 3 }
  ],

  /** 車床部 */
  lathe: [
    { id: 'lat-001', name: '粗車', description: '粗加工去除多餘材料', order: 1 },
    { id: 'lat-002', name: '研磨', description: '初步研磨表面', order: 2 },
    { id: 'lat-003', name: '細處理', description: '細磨達到尺寸精度', order: 3 },
    { id: 'lat-004', name: '特殊處理', description: '依客戶需求之特殊加工', order: 4 }
  ],

  /** 品管部 */
  quality: [
    { id: 'qa-001', name: '檢驗尺寸', description: '比對圖面進行尺寸檢驗', order: 1 },
    { id: 'qa-002', name: '檢驗外觀', description: '檢查外觀缺陷與瑕疵', order: 2 },
    { id: 'qa-003', name: '量測數據報告', description: '整理量測結果並生成報告', order: 3 },
    { id: 'qa-004', name: '打出貨標籤', description: '列印並貼附出貨標籤', order: 4 }
  ],

  /** 包裝部 */
  packaging: [
    { id: 'pkg-001', name: '清潔', description: '最終清潔與去除粉塵', order: 1 },
    { id: 'pkg-002', name: '包裝', description: '包裝並封裝成品', order: 2 }
  ]
};

// ===========================
// 4️⃣ HELPER FUNCTIONS - 輔助函數
// ===========================

// 取得部門資訊
export const getDepartmentById = (id: string): Department | undefined => {
  return departments.find(dept => dept.id === id);
};

export const getDepartmentByName = (name: string): Department | undefined => {
  return departments.find(dept => dept.name === name);
};

// 取得階段資訊
export const getStageById = (id: string): Stage | undefined => {
  return stages.find(stage => stage.id === id);
};

// 取得部門所屬階段
export const getDepartmentStage = (departmentId: string): Stage | undefined => {
  return stages.find(stage => 
    stage.responsibleDepartments.includes(departmentId)
  );
};

// 取得階段下的所有部門
export const getDepartmentsByStage = (stageId: string): Department[] => {
  const stage = stages.find(s => s.id === stageId);
  if (!stage) return [];
  
  return departments.filter(dept => 
    stage.responsibleDepartments.includes(dept.id)
  );
};

// 顏色映射
export const stageColors: Record<string, string> = {
  'material-preparation': 'bg-blue-500',
  'production': 'bg-green-500',
  'quality-inspection': 'bg-purple-500'
};

export const priorityColors = {
  low: 'bg-gray-500',
  medium: 'bg-primary',
  high: 'bg-red-500'
};

export const statusColors = {
  pending: 'bg-gray-500',
  'in-progress': 'bg-primary',
  completed: 'bg-green-500',
  delayed: 'bg-red-500'
};

// ===========================
// 4️⃣ ORDERS - 統一訂單資料 (使用新格式)
// ===========================
export const orders: Order[] = [
  // A類產品 - 全新輪
  {
    // 基本訂單資訊
    id: "ORD-001",
    orderNumber: "PD20250729013",
    drawingNumber: "SD20250729006-002",
    processCode: "T64011",
    processType: "包膠",
    
    // 客戶資訊
    customer: "台灣輪胎股份有限公司",
    customerContact: "張經理 (02)2345-6789",
    deliveryAddress: "台北市信義區信義路五段7號",
    
    // 產品資訊
    product: "工業橡膠輪胎",
    productCode: "WH-A0-65-350",
    productCategory: "A", // 全新輪 - 新膠新鐵我們負責
    dimensions: "350*382*1053",
    quantity: 500,
    completedQuantity: 75,
    
    // 技術規格
    hardness: "65 ± 5",
    specifications: "山寬40溝寬20溝深7，內孔需軸心合過",
    specialRequirements: "需要防滑紋路處理，分批交貨",
    
    // 材料資訊
    materials: [
      {
        code: "HR-A0-65-01-K",
        description: "天然橡膠原料",
        requiredQuantity: 1250,
        issuedQuantity: 187.5,
        unit: "kg",
        supplier: "南帝化工"
      },
      {
        code: "ST-350-38-01",
        description: "鐵輪軸心",
        requiredQuantity: 500,
        issuedQuantity: 75,
        unit: "個",
        supplier: "金豐鑄造"
      }
    ],
    
    // 時程相關
    deliveryDate: "2025-08-25",
    orderDate: "2025-07-19",
    startDate: "2025-07-20",
    endDate: "2025-08-25",
    dueDate: "2025-08-25",
    
    // 狀態與優先級
    stage: "material-preparation",
    priority: "high",
    status: "in-progress",
    
    // 其他資訊
    notes: "客戶要求分批交貨，每批100件，需要品質證明書",
    attachments: ["/enginner_drawing.png", "/enginner_drawing.png"],
    
    // 部門進度追蹤
    departments: [
      { 
        name: "採購部", 
        start: "2025-07-20", 
        end: "2025-07-22", 
        progress: 20, 
        color: "bg-amber-500",
        assignedWorkers: ["陳採購", "李助理"],
        status: "in-progress"
      },
      { 
        name: "倉庫部", 
        start: "2025-07-21", 
        end: "2025-07-23", 
        progress: 10, 
        color: "bg-teal-500",
        assignedWorkers: ["鄭管理員", "謝助理"],
        status: "pending"
      }
    ],
    
    // 品質要求
    qualityGrade: "A",
    inspectionRequirements: "硬度測試、尺寸檢驗、外觀檢查"
  },
  
  // C類產品 - 客戶舊輪
  {
    // 基本訂單資訊
    id: "ORD-002",
    orderNumber: "PD20250730024",
    drawingNumber: "SD20250730008-001",
    processCode: "T64012",
    processType: "除膠包膠",
    
    // 客戶資訊
    customer: "精密機械工業股份有限公司",
    customerContact: "李主任 (04)2234-5678",
    deliveryAddress: "台中市西屯區工業區路88號",
    
    // 產品資訊
    product: "精密滾輪翻新",
    productCode: "WH-C0-70-400",
    productCategory: "C", // 客戶舊輪 - 除膠加膠我們負責
    dimensions: "400*450*1200",
    quantity: 200,
    completedQuantity: 140,
    
    // 技術規格
    hardness: "70 ± 3",
    specifications: "精密度要求±0.1mm，表面光滑度Ra1.6",
    specialRequirements: "舊輪需完全除膠，不得有殘留",
    
    // 材料資訊
    materials: [
      {
        code: "HR-C0-70-02-K",
        description: "高精度橡膠",
        requiredQuantity: 800,
        issuedQuantity: 560,
        unit: "kg",
        supplier: "台橡公司"
      },
      {
        code: "CL-001",
        description: "除膠劑",
        requiredQuantity: 50,
        issuedQuantity: 35,
        unit: "L",
        supplier: "清潔化工"
      }
    ],
    
    // 時程相關
    deliveryDate: "2025-08-30",
    orderDate: "2025-07-24",
    startDate: "2025-07-25",
    endDate: "2025-08-30",
    dueDate: "2025-08-30",
    
    // 狀態與優先級
    stage: "production",
    priority: "medium",
    status: "in-progress",
    
    // 其他資訊
    notes: "需要特殊防滑紋路處理，客戶要求高精度",
    attachments: ["/enginner_drawing.png"],
    
    // 部門進度追蹤
    departments: [
      { 
        name: "包膠部", 
        start: "2025-07-25", 
        end: "2025-07-28", 
        progress: 70, 
        color: "bg-pink-500",
        assignedWorkers: ["劉師傅", "張師傅"],
        status: "in-progress"
      },
      { 
        name: "車床部", 
        start: "2025-07-27", 
        end: "2025-07-29", 
        progress: 40, 
        color: "bg-indigo-500",
        assignedWorkers: ["趙車床", "吳師傅"],
        status: "pending"
      }
    ],
    
    // 品質要求
    qualityGrade: "A",
    inspectionRequirements: "精密度測量、硬度測試、表面粗糙度檢驗"
  },
  
  // B類產品 - 客戶新輪
  {
    // 基本訂單資訊
    id: "ORD-003",
    orderNumber: "PD20250731035",
    drawingNumber: "SD20250731010-003",
    processCode: "T64013",
    processType: "包膠精修",
    
    // 客戶資訊
    customer: "南部重工機械有限公司",
    customerContact: "王工程師 (07)345-6789",
    deliveryAddress: "高雄市前鎮區加工出口區中二路5號",
    
    // 產品資訊
    product: "重工業用橡膠輪",
    productCode: "WH-B0-80-500",
    productCategory: "B", // 客戶新輪 - 新膠我們負責
    dimensions: "500*600*1500",
    quantity: 1000,
    completedQuantity: 900,
    
    // 技術規格
    hardness: "80 ± 5",
    specifications: "重載型設計，承重5噸以上",
    specialRequirements: "急單處理，需要嚴格品質檢驗",
    
    // 材料資訊
    materials: [
      {
        code: "HR-B0-80-03-K",
        description: "重工業橡膠",
        requiredQuantity: 3000,
        issuedQuantity: 2700,
        unit: "kg",
        supplier: "中橡股份"
      }
    ],
    
    // 時程相關
    deliveryDate: "2025-08-15",
    orderDate: "2025-07-27",
    startDate: "2025-07-28",
    endDate: "2025-08-15",
    dueDate: "2025-08-15",
    
    // 狀態與優先級
    stage: "quality-inspection",
    priority: "urgent",
    status: "in-progress",
    
    // 其他資訊
    notes: "急單，需要嚴格品質檢驗，客戶要求完整測試報告",
    attachments: ["/enginner_drawing.png", "/enginner_drawing.png"],
    
    // 部門進度追蹤
    departments: [
      { 
        name: "品管部", 
        start: "2025-07-28", 
        end: "2025-07-29", 
        progress: 90, 
        color: "bg-purple-600",
        assignedWorkers: ["鄭檢驗員", "劉檢驗員"],
        status: "in-progress"
      },
      { 
        name: "包裝部", 
        start: "2025-07-29", 
        end: "2025-07-30", 
        progress: 60, 
        color: "bg-gray-600",
        assignedWorkers: ["高包裝員", "葉助理"],
        status: "pending"
      }
    ],
    
    // 品質要求
    qualityGrade: "A",
    inspectionRequirements: "承重測試、硬度測試、尺寸檢驗、材質證明"
  }
];

// ===========================
// 5️⃣ ORDER ADAPTER FUNCTIONS - 訂單格式轉換
// ===========================

/**
 * Convert master Order to UnifiedOrder (for Workflow.tsx)
 */
export const orderToUnifiedOrder = (order: Order): UnifiedOrder => {
  // Calculate overall progress from departments
  const overallProgress = order.departments 
    ? order.departments.reduce((sum, dept) => sum + dept.progress, 0) / order.departments.length / 100
    : 0;

  // Get current stage based on order.stage
  const currentStageId = order.stage;

  // Create simplified stages array for UnifiedOrder
  const stages = order.departments ? [{
    stageId: order.stage,
    stageName: getStageById(order.stage)?.name || 'Unknown Stage',
    group: 1,
    plannedStart: order.startDate,
    plannedEnd: order.endDate,
    progress: overallProgress,
    status: overallProgress === 0 ? 'pending' : overallProgress === 1 ? 'completed' : 'in-progress',
    departments: order.departments.map((dept, index) => ({
      departmentId: getDepartmentByName(dept.name)?.id || `dept-${index}`,
      departmentName: dept.name,
      group: 1,
      plannedStart: dept.start,
      plannedEnd: dept.end,
      progress: dept.progress / 100,
      operations: [] // Could be populated from workItems if needed
    }))
  }] : [];

  return {
    id: order.id,
    customer: order.customer,
    customerContact: order.customerContact,
    product: order.product,
    quantity: order.quantity,
    completedQuantity: order.completedQuantity,
    dimensions: order.dimensions,
    specifications: order.specifications,
    priority: order.priority,
    orderDate: order.orderDate,
    dueDate: order.dueDate,
    plannedStart: order.startDate,
    plannedEnd: order.endDate,
    currentStageId,
    overallProgress,
    stages,
    deliveryAddress: order.deliveryAddress,
    estimatedCost: 0, // Default value since new Order doesn't have this field
    notes: order.notes
  };
};

/**
 * Convert master Order to OrderData (for Gantt charts)
 * 甘特圖需要顯示完整的生產計劃：所有階段、所有部門、所有工序
 */
export const orderToOrderData = (order: Order): OrderData => {
  // Calculate overall progress from departments
  const overallProgress = order.departments 
    ? order.departments.reduce((sum, dept) => sum + dept.progress, 0) / order.departments.length / 100
    : 0;

  // Create complete stages for all 3 stages (準備材料, 生產, 品檢)
  const allStages = stages.map((stage, stageIndex) => {
    // 取得這個階段負責的部門
    const stageDepartments = getDepartmentsByStage(stage.id);
    
    // 為每個部門創建完整的工序計劃
    const departments = stageDepartments.map((dept, deptIndex) => {
      // 取得部門的工序範本
      const operations = workItemTemplates[dept.id as keyof typeof workItemTemplates] || [];
      
      // 模擬每個工序的時間規劃（基於訂單時間範圍）
      const departmentStartDate = new Date(order.startDate);
      departmentStartDate.setDate(departmentStartDate.getDate() + stageIndex * 5 + deptIndex * 2);
      
      const operationsData = operations.map((op, opIndex) => {
        const operationStart = new Date(departmentStartDate);
        operationStart.setHours(8 + opIndex * 2); // 每個工序間隔2小時
        
        const operationEnd = new Date(operationStart);
        operationEnd.setHours(operationStart.getHours() + 2); // 每個工序2小時
        
        return {
          id: `${order.id}-${stage.id}-${dept.id}-${op.id}`,
          name: op.name,
          assignee: '待分配', // 可以從 workItems 中匹配
          start: operationStart.toISOString(),
          end: operationEnd.toISOString(),
          progress: stage.id === order.stage ? 0.6 : 0 // 當前階段有進度，其他為0
        };
      });

      const deptStartDate = new Date(departmentStartDate);
      const deptEndDate = new Date(departmentStartDate);
      deptEndDate.setDate(deptEndDate.getDate() + 1); // 部門工作期間1天

      return {
        id: dept.id,
        name: dept.name,
        group: stageIndex + 1,
        start: deptStartDate.toISOString(),
        end: deptEndDate.toISOString(),
        progress: stage.id === order.stage ? 0.5 : 0, // 當前階段有進度
        operations: operationsData
      };
    });

    const stageStartDate = new Date(order.startDate);
    stageStartDate.setDate(stageStartDate.getDate() + stageIndex * 5);
    
    const stageEndDate = new Date(stageStartDate);
    stageEndDate.setDate(stageEndDate.getDate() + 4); // 每個階段4天

    return {
      id: stage.id,
      name: stage.name,
      group: stageIndex + 1,
      start: stageStartDate.toISOString(),
      end: stageEndDate.toISOString(),
      progress: stage.id === order.stage ? 0.7 : 0, // 當前階段有進度
      departments
    };
  });

  return {
    orderId: order.id,
    customer: order.customer,
    product: order.product,
    quantity: order.quantity,
    priority: order.priority,
    progress: overallProgress,
    start: order.startDate,
    end: order.endDate,
    dueDate: order.dueDate,
    stages: allStages
  };
};

/**
 * Get orders in UnifiedOrder format
 */
export const getUnifiedOrders = (): UnifiedOrder[] => {
  return orders.map(orderToUnifiedOrder);
};

/**
 * Get orders in OrderData format  
 */
export const getOrderDataList = (): OrderData[] => {
  return orders.map(orderToOrderData);
};